import React, { useEffect, useState } from 'react';
import { X, User, Users } from 'lucide-react';
import { getReassignableUsers, reassignCase } from '../../services/caseApi';

const f = "'Inter', system-ui, -apple-system, sans-serif";

export default function ReassignModal({ isOpen, onClose, caseId, caseData, user, onReassigned }) {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !caseId) return;
    setSelectedUserId('');
    setRemarks('');
    setError('');
    setAvailableUsers([]);
    setFetching(true);
    getReassignableUsers(caseId)
      .then((res) => {
        if (res.success) {
          setAvailableUsers(res.data?.availableUsers || []);
        } else {
          setError(res.message || 'Failed to load users');
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || err.message || 'Failed to load users');
      })
      .finally(() => setFetching(false));
  }, [isOpen, caseId]);

  const handleSubmit = async () => {
    if (!selectedUserId) {
      setError('Please select a new assignee');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await reassignCase(caseId, { newAssigneeId: selectedUserId, remarks: remarks.trim() });
      if (res.success) {
        onReassigned?.(res.data);
        onClose();
      } else {
        setError(res.message || 'Failed to reassign');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to reassign');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentAssignee = caseData?.assignee_name || 'Unassigned';
  const currentLevel = caseData?.escalation_level || 'L1';
  const noUsers = !fetching && availableUsers.length === 0;
  const canSubmit = !loading && !fetching && !noUsers && selectedUserId;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, fontFamily: f }}>
      <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 460, maxWidth: '90%', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563EB' }}>
              <Users size={18} />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1E293B', margin: 0 }}>Reassign Case</h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' }}>Current Assignee</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, borderRadius: 10, background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#1E293B', fontSize: 14 }}>
              <User size={16} color="#64748B" />
              <span>{currentAssignee}</span>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' }}>Current Level</label>
            <div style={{ padding: 12, borderRadius: 10, background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#1E293B', fontSize: 14, fontWeight: 600 }}>
              {currentLevel}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' }}>New Assignee</label>
            {fetching ? (
              <div style={{ padding: 12, color: '#64748B', fontSize: 14 }}>Loading users...</div>
            ) : noUsers ? (
              <div style={{ padding: 12, borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', fontSize: 14 }}>
                No other users available at this level.
              </div>
            ) : (
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={{ width: '100%', padding: 12, borderRadius: 10, border: '1px solid #E2E8F0', fontSize: 14, fontFamily: f, color: '#1E293B', background: '#FFFFFF', outline: 'none' }}
              >
                <option value="">Select assignee...</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' }}>Remarks <span style={{ fontWeight: 400, color: '#94A3B8' }}>(optional)</span></label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add remarks for the reassignment..."
              rows={3}
              style={{ width: '100%', padding: 12, borderRadius: 10, border: '1px solid #E2E8F0', fontSize: 14, fontFamily: f, color: '#1E293B', background: '#FFFFFF', outline: 'none', resize: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {error && (
          <div style={{ padding: 10, borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{ flex: 1, height: 42, borderRadius: 10, background: '#F1F5F9', color: '#0F172A', border: '1px solid #E2E8F0', fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{ flex: 1, height: 42, borderRadius: 10, background: canSubmit ? '#2563EB' : '#CBD5E1', color: '#FFFFFF', border: 'none', fontSize: 14, fontWeight: 600, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
          >
            {loading ? 'Reassigning...' : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
}
